import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DishService, Dish } from '../services/dish.service';
import { HttpClientModule } from '@angular/common/http';
import { TakeawayService } from '../services/takeaway.service';
import { PaymentService } from '../services/payment.service';
import { HttpClient } from '@angular/common/http';
import { OrderType } from '../../shared/enums/order-type.enum';
import { AlertService } from '../services/alert.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-takeaway-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, MatTooltipModule],
  templateUrl: './takeaway-popup.component.html',
  styleUrls: ['./takeaway-popup.component.scss'],
})
export class TakeawayPopupComponent {
  @Output() closePopup = new EventEmitter<void>();

  @Input() dishes: Dish[] = [];
  @Input() restaurantId!: string;
  @Input() gst: number = 0;

  packagingChargePerItem: number = 10;

  cart: any[] = [];
  categories: string[] = [];
  selectedCategory: string = '';
  menuOpen = false;
  cartOpen = false;
  searchQuery = '';
  loading: boolean = false;
  searchDebounceTimer: any;

  checkoutOpen = false;
  customerName = '';
  customerContact = '';
  paymentLoading = false;
  customerEmail = '';
  showCookingInstruction = false;
  cookingInstruction = '';

  toggleCheckoutPopup() {
    this.checkoutOpen = !this.checkoutOpen;
  }

  mobileValid() {
    return /^[6-9][0-9]{9}$/.test(this.customerContact);
  }

  gstAmount(): number {
    return Math.round((this.totalPrice() * this.gst) / 100);
  }

  packagingCharge(): number {
    return this.totalItems() * this.packagingChargePerItem;
  }

  payableAmount(): number {
    return Math.round(this.totalPrice() + this.gstAmount() + this.packagingCharge());
  }

  constructor(
    private dishService: DishService,
    private http: HttpClient,
    private paymentService: PaymentService,
    private takeawayService: TakeawayService,
    private alertService: AlertService
  ) { }

  fetchDishes() {
    this.loading = true;

    this.dishService
      .getDishes(this.restaurantId, this.searchQuery, this.selectedCategory)
      .subscribe({
        next: (dishes: Dish[]) => {
          this.dishes = dishes;

          if (this.categories.length === 0) {
            this.categories = [...new Set(dishes.map((d) => d.category))];
          }

          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading dishes', err);
          this.loading = false;
        },
      });
  }

  ngOnInit() {
    this.fetchDishes();
  }

  toggleMenuPopup() {
    this.menuOpen = !this.menuOpen;
  }

  toggleCartPopup() {
    this.showCookingInstruction = false;
    this.cartOpen = !this.cartOpen;
  }

  selectCategory(cat: string) {
    this.selectedCategory = cat;
    this.menuOpen = false;
    this.fetchDishes();
  }

  filteredDishes(): Dish[] {
    if (!this.selectedCategory) return this.dishes;
    return this.dishes.filter((d) => d.category === this.selectedCategory);
  }

  getDishInCart(dish: Dish) {
    return this.cart.find((i) => i.id === dish.id);
  }

  addToCart(dish: Dish) {
    const existing = this.getDishInCart(dish);
    if (existing) existing.qty++;
    else this.cart.push({ ...dish, qty: 1 });
  }

  increaseQty(item: any) {
    item.qty++;
  }

  decreaseQty(item: any) {
    if (item.qty > 1) item.qty--;
    else this.cart = this.cart.filter((i) => i.id !== item.id);
  }

  totalItems(): number {
    return this.cart.reduce((sum, i) => sum + i.qty, 0);
  }

  totalPrice(): number {
    return this.cart.reduce((sum, i) => sum + i.price * i.qty, 0);
  }

  close() {
    this.closePopup.emit();
  }

  nextImage(dish: Dish, event: Event) {
    event.stopPropagation();
    const total = dish.dishImages.length;
    dish.currentImageIndex = (dish.currentImageIndex + 1) % total;
  }

  prevImage(dish: Dish, event: Event) {
    event.stopPropagation();
    const total = dish.dishImages.length;
    dish.currentImageIndex = (dish.currentImageIndex - 1 + total) % total;
  }

  goToImage(dish: Dish, index: number) {
    dish.currentImageIndex = index;
  }

  skeletonArray(): number[] {
    const count = this.dishes?.length || 8;
    return Array(Math.min(count, 8)).fill(0);
  }

  onSearchChange() {
    const query = this.searchQuery.trim();

    clearTimeout(this.searchDebounceTimer);

    this.searchDebounceTimer = setTimeout(() => {
      if (query.length >= 3 || query.length === 0) {
        this.fetchDishes();
      }
    }, 300);
  }

  toggleCookingInstruction(): void {
    this.showCookingInstruction = !this.showCookingInstruction;
  }

  async submitOrder(form: any) {
    if (form.invalid) {
      return;
    }

    if (this.cart.length === 0) {
      this.alertService.toast({
        icon: 'warning',
        title: 'Your cart is empty. Please add some dishes before ordering.',
      });
      return;
    }

    this.loading = true;

    const orderBody = {
      restaurantId: this.restaurantId,
      items: this.cart.map((i) => ({
        dishId: i.id,        // or i._id depending on your Dish model
        quantity: i.qty,
      })),
      customerName: this.customerName?.trim(),
      customerContact: this.customerContact,
      customerEmail: this.customerEmail?.trim(),
      amount: this.payableAmount(),
      gst: this.gstAmount(),
      packagingCharges: this.packagingCharge(),
      cookingInstruction: this.cookingInstruction?.trim().replace(/\n+/g, ', ') || '',
    };

    try {
      const response = await this.takeawayService.createOrder(orderBody);

      if (response) {
        this.paymentLoading = true;

        const paymentResult = await this.paymentService.startPayment(
          response._id,
          this.payableAmount(),
          this.customerName,
          this.customerContact,
          this.customerEmail,
          OrderType.Takeaway
        );

        if (!paymentResult?.success) {
          this.alertService.toast({
            icon: 'error',
            title: 'Payment Cancelled',
          });
          this.paymentLoading = false;
          return;
        }

        this.alertService.toast({
          icon: 'success',
          title: 'Order placed successfully!',
        });

        this.checkoutOpen = false;
        this.cartOpen = false;
        this.cart = [];
        this.closePopup.emit();
      }
    } catch (err: any) {
      console.error('❌ Order submission failed:', err);
      this.paymentLoading = false;

      this.alertService.toast({
        icon: 'error',
        title: 'Payment Failed',
      });
    } finally {
      this.loading = false;
    }
  }

  isTextClamped(el: HTMLElement | null | undefined): boolean {
    if (!el) return false;

    // scrollHeight > clientHeight means content is overflowing (clamped)
    return el.scrollHeight > el.clientHeight + 1;
  }

}
